/**
 * Review a company's request for more, and answer it — Share, Not now or
 * Decline. (D2, 4 Oct 2026)
 *
 * The request's twin of `InvitationReviewDialog`, opened from Home's card and
 * the bell's list. There is no journey to choose: the request is about a
 * relationship that already exists, and Share widens THAT relationship.
 */
import { useTranslation } from '@tonnie/locale';
import type { MyPendingInvitation } from '@tonnie/sdk';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@tonnie/ui';
import { useRef } from 'react';

import { CompanyMark } from '@/features/platform/invitations/components/CompanyMark';

import { useShareRequestTitle } from '../hooks/use-share-request-title';
import { ShareRequestAnswerPanel } from './ShareRequestAnswerPanel';

export function ShareRequestDialog({
  invitation,
  open,
  onOpenChange,
}: {
  /** A pending invitation that IS a request (`invitation.request` set). */
  invitation: MyPendingInvitation & { request: NonNullable<MyPendingInvitation['request']> };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const company = invitation.companyName;
  const title = useShareRequestTitle(invitation.request, company);
  // Read at close time, never rendered: the dialog stays open while an answer is on its way.
  const busy = useRef(false);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy.current && onOpenChange(next)}>
      <DialogContent
        className="sm:max-w-lg"
        closeLabel={t('common.close')}
        data-testid="share-request-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <CompanyMark name={company} logo={invitation.companyLogo} />
            <DialogTitle className="text-left">{title}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {t('invitations.request.eyebrow')}
          </DialogDescription>
        </DialogHeader>
        <ShareRequestAnswerPanel
          invitation={invitation}
          onAnswered={() => onOpenChange(false)}
          onBusyChange={(next) => {
            busy.current = next;
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
